import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RequestStatus } from '../../shared/enums/request-status';
import { SortOrder } from '../../shared/enums/order';
import {
  EnrichmentStatus,
  EnrichmentType,
  EnrichmentComponent,
  NewEnrichmentStep,
  DateRangeFilter,
  PEOPLE_EXPORT_COLUMNS,
} from './enums/csv-enrichment.enums';
import {
  CsvEnrichmentState,
  ColumnMapping,
  UploadedFileInfo,
} from './types/csv-enrichment.types';
import {
  getEnrichmentJobs,
  getEnrichmentStats,
  createEnrichmentJob,
  deleteEnrichmentJob,
  getEnrichmentJobStatus,
  getEnrichmentJobRecords,
  loadMoreEnrichmentJobRecords,
  ENRICHMENT_RECORDS_PAGE_SIZE,
} from './extra-actions';

const initialState: CsvEnrichmentState = {
  jobs: [],
  jobsRequestStatus: RequestStatus.Ideal,
  stats: { totalFiles: 0, peopleEnriched: 0, companyEnriched: 0 },
  statsRequestStatus: RequestStatus.Ideal,

  dateRange: DateRangeFilter.THIRTY_DAYS,
  searchQuery: '',
  statusFilter: null,
  typeFilter: null,
  page: 1,
  pageSize: 25,
  totalJobs: 0,

  isModalOpen: false,
  currentStep: NewEnrichmentStep.SELECT_TYPE,
  selectedType: null,
  uploadedFile: null,
  columnMapping: {
    firstName: null,
    lastName: null,
    companyName: null,
    companyDomain: null,
    linkedinUrl: null,
  },
  selectedComponent: null,
  exportColumns: [...PEOPLE_EXPORT_COLUMNS],
  createJobRequestStatus: RequestStatus.Ideal,

  deleteJobRequestStatus: RequestStatus.Ideal,
  exportJobRequestStatus: RequestStatus.Ideal,

  pollingJobIds: [],

  lastCreatedJobId: null,
  shouldPollStatusPage: null,

  sortBy: null,
  sortOrder: null,

  records: [],
  recordColumns: [],
  recordsRequestStatus: RequestStatus.Ideal,
  recordsJob: null,
  recordsCurrentPage: 1,
  recordsHasMore: false,
  recordsIsLoadingMore: false,
};

const csvEnrichmentSlice = createSlice({
  name: 'csvEnrichment',
  initialState,
  reducers: {
    setDateRange(state, action: PayloadAction<DateRangeFilter>) {
      state.dateRange = action.payload;
      state.page = 1;
    },
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
      state.page = 1;
    },
    setStatusFilter(state, action: PayloadAction<EnrichmentStatus | null>) {
      state.statusFilter = action.payload;
      state.page = 1;
    },
    setTypeFilter(state, action: PayloadAction<EnrichmentType | null>) {
      state.typeFilter = action.payload;
      state.page = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
      state.jobsRequestStatus = RequestStatus.Ideal;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.page = 1;
      state.jobsRequestStatus = RequestStatus.Ideal;
    },
    setSort(
      state,
      action: PayloadAction<{ sortBy: string; sortOrder: SortOrder } | null>,
    ) {
      state.sortBy = action.payload?.sortBy ?? null;
      state.sortOrder = action.payload?.sortOrder ?? null;
      state.page = 1;
    },
    resetWizard(state) {
      state.currentStep = NewEnrichmentStep.SELECT_TYPE;
      state.selectedType = null;
      state.uploadedFile = null;
      state.columnMapping = {
        firstName: null,
        lastName: null,
        companyName: null,
        companyDomain: null,
        linkedinUrl: null,
      };
      state.selectedComponent = null;
      state.exportColumns = [...PEOPLE_EXPORT_COLUMNS];
      state.createJobRequestStatus = RequestStatus.Ideal;
      state.lastCreatedJobId = null;
    },
    setCurrentStep(state, action: PayloadAction<NewEnrichmentStep>) {
      state.currentStep = action.payload;
    },
    setSelectedType(state, action: PayloadAction<EnrichmentType>) {
      state.selectedType = action.payload;
    },
    setUploadedFile(state, action: PayloadAction<UploadedFileInfo | null>) {
      state.uploadedFile = action.payload;
    },
    setColumnMapping(state, action: PayloadAction<ColumnMapping>) {
      state.columnMapping = action.payload;
    },
    setSelectedComponent(
      state,
      action: PayloadAction<EnrichmentComponent | null>,
    ) {
      state.selectedComponent = action.payload;
    },
    setExportColumns(state, action: PayloadAction<string[]>) {
      state.exportColumns = action.payload;
    },
    clearLastCreatedJobId(state) {
      state.lastCreatedJobId = null;
    },
    resetRecords(state) {
      state.records = [];
      state.recordsRequestStatus = RequestStatus.Ideal;
      state.recordsJob = null;
      state.recordsCurrentPage = 1;
      state.recordsHasMore = false;
      state.recordsIsLoadingMore = false;
      state.shouldPollStatusPage = null;
    },
    resetJobsRequestStatus(state) {
      state.jobsRequestStatus = RequestStatus.Ideal;
    },
  },
  extraReducers: (builder) => {
    // getEnrichmentJobs
    builder.addCase(getEnrichmentJobs.pending, (state) => {
      if (state.jobsRequestStatus === RequestStatus.Ideal) {
        state.jobsRequestStatus = RequestStatus.Pending;
      }
    });
    builder.addCase(getEnrichmentJobs.fulfilled, (state, action) => {
      state.jobsRequestStatus = RequestStatus.Succeeded;
      state.jobs = action.payload.jobs;
      state.totalJobs = action.payload.total;
      state.pollingJobIds = action.payload.jobs
        .filter(
          (j) =>
            j.status === EnrichmentStatus.PROCESSING ||
            j.status === EnrichmentStatus.QUEUED,
        )
        .map((j) => j.id);
    });
    builder.addCase(getEnrichmentJobs.rejected, (state) => {
      state.jobsRequestStatus = RequestStatus.Failed;
    });

    // getEnrichmentStats
    builder.addCase(getEnrichmentStats.pending, (state) => {
      state.statsRequestStatus = RequestStatus.Pending;
    });
    builder.addCase(getEnrichmentStats.fulfilled, (state, action) => {
      state.statsRequestStatus = RequestStatus.Succeeded;
      state.stats = action.payload;
    });
    builder.addCase(getEnrichmentStats.rejected, (state) => {
      state.statsRequestStatus = RequestStatus.Failed;
    });

    // createEnrichmentJob
    builder.addCase(createEnrichmentJob.pending, (state) => {
      state.createJobRequestStatus = RequestStatus.Pending;
    });
    builder.addCase(createEnrichmentJob.fulfilled, (state, action) => {
      state.createJobRequestStatus = RequestStatus.Succeeded;
      state.lastCreatedJobId = action.payload.id;
      state.shouldPollStatusPage = action.payload.shouldPoll ?? null;
      state.jobs = [action.payload, ...state.jobs];
      state.totalJobs += 1;
      if (
        action.payload.status === EnrichmentStatus.PROCESSING ||
        action.payload.status === EnrichmentStatus.QUEUED
      ) {
        state.pollingJobIds = [...state.pollingJobIds, action.payload.id];
      }
      // Clear stale records from any previously viewed job
      state.records = [];
      state.recordColumns = [];
      state.recordsRequestStatus = RequestStatus.Ideal;
      state.recordsJob = null;
      state.recordsCurrentPage = 1;
      state.recordsHasMore = false;
      state.recordsIsLoadingMore = false;
      // Reset wizard state — navigation handled in container
      state.currentStep = NewEnrichmentStep.SELECT_TYPE;
      state.selectedType = null;
      state.uploadedFile = null;
      state.columnMapping = {
        firstName: null,
        lastName: null,
        companyName: null,
        companyDomain: null,
        linkedinUrl: null,
      };
      state.selectedComponent = null;
    });
    builder.addCase(createEnrichmentJob.rejected, (state) => {
      state.createJobRequestStatus = RequestStatus.Failed;
    });

    // deleteEnrichmentJob
    builder.addCase(deleteEnrichmentJob.pending, (state) => {
      state.deleteJobRequestStatus = RequestStatus.Pending;
    });
    builder.addCase(deleteEnrichmentJob.fulfilled, (state, action) => {
      state.deleteJobRequestStatus = RequestStatus.Succeeded;
      state.jobs = state.jobs.filter((j) => j.id !== action.payload.id);
      state.totalJobs = Math.max(state.totalJobs - 1, 0);
      state.pollingJobIds = state.pollingJobIds.filter(
        (id) => id !== action.payload.id,
      );
    });
    builder.addCase(deleteEnrichmentJob.rejected, (state) => {
      state.deleteJobRequestStatus = RequestStatus.Failed;
    });

    // getEnrichmentJobRecords
    // Only set Pending on the initial fetch — skip during polling re-fetches
    // to avoid flicker between skeleton and data/empty state.
    builder.addCase(getEnrichmentJobRecords.pending, (state) => {
      if (state.recordsRequestStatus === RequestStatus.Ideal) {
        state.recordsRequestStatus = RequestStatus.Pending;
      }
    });
    builder.addCase(getEnrichmentJobRecords.fulfilled, (state, action) => {
      state.recordsRequestStatus = RequestStatus.Succeeded;
      const {
        records: firstPage,
        columns,
        job,
        preserveAccumulatedPages,
      } = action.payload;

      const shouldMergeLoadedPages =
        preserveAccumulatedPages &&
        (state.recordsCurrentPage > 1 ||
          state.records.length > ENRICHMENT_RECORDS_PAGE_SIZE);

      if (shouldMergeLoadedPages) {
        state.records = [
          ...firstPage,
          ...state.records.slice(ENRICHMENT_RECORDS_PAGE_SIZE),
        ];
      } else {
        state.records = firstPage;
        state.recordsCurrentPage = 1;
      }

      state.recordColumns = columns || [];
      state.recordsJob = job ?? null;

      const availableRecords = job?.enrichedRecords ?? job?.totalRecords ?? 0;
      state.recordsHasMore = state.records.length < availableRecords;
    });
    builder.addCase(getEnrichmentJobRecords.rejected, (state) => {
      state.recordsRequestStatus = RequestStatus.Failed;
    });

    // loadMoreEnrichmentJobRecords
    builder.addCase(loadMoreEnrichmentJobRecords.pending, (state) => {
      state.recordsIsLoadingMore = true;
    });
    builder.addCase(loadMoreEnrichmentJobRecords.fulfilled, (state, action) => {
      state.recordsIsLoadingMore = false;
      const newRecords = action.payload.records;
      if (newRecords.length === 0) {
        state.recordsHasMore = false;
        return;
      }
      state.records = [...state.records, ...newRecords];
      state.recordsCurrentPage += 1;
      const availableRecords =
        state.recordsJob?.enrichedRecords ??
        state.recordsJob?.totalRecords ??
        0;
      state.recordsHasMore = state.records.length < availableRecords;
    });
    builder.addCase(loadMoreEnrichmentJobRecords.rejected, (state) => {
      state.recordsIsLoadingMore = false;
    });

    // getEnrichmentJobStatus (polling)
    builder.addCase(getEnrichmentJobStatus.fulfilled, (state, action) => {
      const {
        id,
        status,
        progress,
        enrichedRecords,
        matchPercentage,
      } = action.payload;
      const jobIndex = state.jobs.findIndex((j) => j.id === id);
      if (jobIndex !== -1) {
        state.jobs[jobIndex] = {
          ...state.jobs[jobIndex],
          status,
          progress,
          enrichedRecords,
          matchPercentage,
        };
      }
      if (state.recordsJob?.id === id) {
        state.recordsJob = {
          ...state.recordsJob,
          status,
          progress,
          enrichedRecords,
          matchPercentage,
        };
        const availableRecords =
          state.recordsJob.enrichedRecords ??
          state.recordsJob.totalRecords ??
          0;
        state.recordsHasMore = state.records.length < availableRecords;
      }
      // Remove from polling if terminal state
      if (
        status === EnrichmentStatus.COMPLETED ||
        status === EnrichmentStatus.FAILED ||
        status === EnrichmentStatus.CANCELLED
      ) {
        state.pollingJobIds = state.pollingJobIds.filter((pid) => pid !== id);
      }
    });
  },
});

export const {
  setDateRange,
  setSearchQuery,
  setStatusFilter,
  setTypeFilter,
  setPage,
  setPageSize,
  setSort,
  resetWizard,
  setCurrentStep,
  setSelectedType,
  setUploadedFile,
  setColumnMapping,
  setSelectedComponent,
  setExportColumns,
  clearLastCreatedJobId,
  resetRecords,
  resetJobsRequestStatus,
} = csvEnrichmentSlice.actions;

export default csvEnrichmentSlice.reducer;
